/**
 * Le monde de la démo — l'état, et les gestes qui le font bouger.
 *
 * Il n'est pas persisté, et c'est voulu : **recharger la page rend un monde
 * neuf**, prêt pour une nouvelle prise. C'est le bouton « recommencer » de la
 * démonstration, et il ne coûte pas une ligne.
 *
 * Les créatrices, elles, sont les VRAIES : `DemoBridge` y verse la liste
 * Convex du compte connecté. Une démo sur des créatrices inventées ne
 * ressemblerait pas au cockpit de Kayla — or c'est tout l'intérêt.
 *
 * 🪤 Les formes servies ici sont celles du contrat Cyrano
 * (`ApiConversationListItem`, `ApiMessage`, `ApiConversationDetail`), importées
 * en `import type` depuis le panneau. L'import de type est effacé à la
 * compilation : aucun cycle d'import avec le client HTTP, et le jour où un
 * champ obligatoire apparaît côté contrat, c'est `tsc` qui le dit ici.
 */
import type {
  ApiConversationDetail,
  ApiConversationListItem,
  ApiFanNote,
  ApiFanProfile,
  ApiFanTag,
  ApiFanTagAssignment,
  ApiMessage,
} from "@/panels/conversations/atoms";
import type { FanTagLite } from "@/panels/conversations/mockData";

import { construireLesConversations } from "./fabrique";
import type { Dit, Langue } from "./langues";
import { toutAnnuler } from "./minuteries";

export interface CreatriceDemo {
  /**
   * L'identifiant CYRANO (UUID Postgres), pas l'id Convex.
   *
   * 🩸 C'était l'id Convex jusqu'au 19-08-2026, et ça a mordu : le panneau fan
   * passe ce `creator_id` à des routes qui, elles, ne sont PAS détournées par
   * la démo — le Vault, les flows, le catalogue d'emojis. Elles répondaient
   * 400/422 sur un id qu'elles ne connaissent pas, et le compositeur ouvrait
   * un Vault vide. Avec l'identifiant Cyrano, ces routes servent les VRAIS
   * médias de la créatrice : c'est ce qui fait que la démo ressemble au
   * cockpit — parce que la moitié de ce qu'elle montre est vraie.
   *
   * Il se résout par `GET /api/creators` (`{id, slug}`), la route faite pour
   * ça. À défaut, on retombe sur l'id Convex : la grille marche, les routes
   * créatrice non.
   */
  id: string;
  /** Le slug — c'est LUI qui joint la couleur et l'avatar côté cockpit. */
  slug: string;
  nom: string;
  avatar_url: string | null;
}

/**
 * Le GESTE de l'opératrice auquel le fan réagit.
 *
 * C'est la seule chose qu'une scène ait besoin de savoir : ce qui vient d'être
 * fait, pas ce qui a été écrit. On ne lit jamais le texte de Kayla pour
 * décider — une scène filmée doit avancer sur ce qu'elle FAIT (envoyer la
 * carte, poser une transaction, rendre la main), sinon la prise dépend d'une
 * tournure.
 */
export type Geste = "texte" | "media" | "menu" | "transaction" | "cyrano";

/**
 * Le PRÉNOM et la LANGUE d'un fan suffisent à lui faire dire quelque chose :
 * une bulle de fan est un `Dit` — ce qu'il écrit, et ce que ça veut dire en
 * français. En français les deux sont la même phrase.
 */
export type { Dit, Langue };

/**
 * Un TEMPS de la scène : ce que le fan dit quand le geste attendu tombe, et
 * ce qu'il dit quand il ne tombe pas.
 *
 * Même forme que les temps de l'épreuve de candidature
 * (`regie-test/candidate/vente/scenario.ts`, `REPLIQUES` / `RELANCES`), et
 * pour la même raison : « écrire ici la réponse AU GESTE QUI VIENT D'ÊTRE
 * FAIT, jamais au suivant ». Un fan qui répond au temps d'après fait sauter
 * une étape à l'image, et la démonstration perd le temps qu'elle voulait
 * montrer.
 */
export interface Repartie {
  /** Le geste qui ouvre ce temps. */
  sur: Geste;
  /** Ce qu'il écrit alors — une bulle par entrée, dans l'ordre. */
  dit: Array<string | Dit>;
  /**
   * Ce qu'il dit quand elle fait AUTRE CHOSE — il relance, et le temps reste
   * ouvert. Sans ça, une phrase de trop avant l'envoi du menu consommerait le
   * temps du menu, et la carte partirait dans le silence.
   */
  relance?: Array<string | Dit>;
}

export interface ConversationDemo {
  /** La ligne servie par la liste. */
  fiche: ApiConversationListItem;
  fan: ApiFanProfile;
  messages: ApiMessage[];
  note: ApiFanNote;
  tags: FanTagLite[];
  /**
   * Ce que le fan répondra, une réplique par message envoyé par l'opératrice.
   * Vide = le fan se tait (c'est un état légitime : tous les fans ne répondent
   * pas dans la seconde).
   */
  repliques: Array<string | Dit>;
  /** Ce que Cyrano écrira au prochain « Reprendre », dans l'ordre. */
  brouillons: string[];
  /** La langue dans laquelle il écrit — celle de sa carte. */
  langue: Langue;
  /** Les flows qu'il a déjà reçus, par NOM : un flow ne se lance qu'une fois. */
  flowsRecus: string[];
  /**
   * Ce qu'il répond quand le flow se termine sur « tu vas devoir me payer ».
   * C'est là que le chatteur reprend : sans cette réplique, un flow fini
   * tomberait dans un silence que personne ne filme.
   */
  apresLeFlow?: Dit[];
  /** Ce qu'il dit au fil d'un flow, quand le flow attend sa réponse. */
  pendantLeFlow?: Dit[];
  /** La scène qui commence QUAND le flow est fini — le prix, le lien, le
   *  paiement. Posée sur `script` à la fin du flow, pas avant : un « hey »
   *  écrit avant de lancer le flow ne doit pas consommer le temps du prix. */
  scriptApresLeFlow?: Repartie[];
  /** Quand il a payé pour la dernière fois pendant la démo — Francis passe
   *  alors à la deuxième marche. */
  payeLe?: string;
  /**
   * La SCÈNE, quand il y en a une : une suite de temps qui n'avancent que sur
   * le geste qu'ils attendent (cf `Repartie`). Absente sur le fond de grille,
   * qui se contente de `repliques`.
   *
   * Elle s'épuise ; quand le dernier temps est joué, le fan retombe sur
   * `repliques`. C'est voulu — une prise qui déborde du script doit trouver un
   * fan qui parle encore, pas un fan mort.
   */
  script?: Repartie[];
  /** L'index de la relance servie la fois d'avant — pour ne pas répéter la
   *  même phrase deux fois de suite quand elle insiste. */
  relanceSuivante?: number;
  /**
   * Vrai pour les conversations SCRIPTÉES — celles qu'on filme.
   *
   * 🩸 La vie de fond les épargne, et elle le décidait sur un COMPTE écrit à
   * la main (`PREMIERE_DE_FOND = 14`). La quinzième scène est arrivée le
   * 19-08-2026 et le compte est resté à quatorze : un fan de passage pouvait
   * poser un message dans « la demande de partie », c'est-à-dire précisément
   * dans la scène dont la démonstration est que RIEN n'arrive.
   */
  scene: boolean;
}

interface Monde {
  creatrices: CreatriceDemo[];
  /** Ordre de construction (le tri de la grille fait le reste). */
  ordre: string[];
  parId: Map<string, ConversationDemo>;
  /**
   * 🎯 MA FILE — ce que la chatteuse s'est ajouté depuis « Discussions ».
   *
   * Kayla, 07-10-2026 : « je lance le shift, il y a 32 personnes dans ma
   * file. Normalement, quand on lance le shift, il n'y a personne ». Elle
   * naît VIDE : c'est le geste « Ajouter » qui la remplit, comme au poste.
   */
  maFile: Set<string>;
}

let monde: Monde | null = null;
let signatureDesCreatrices = "";

/* ────────────────────────────────────────────────────────────────────────── */
/* Cycle de vie                                                               */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Verse les vraies créatrices dans le monde. Rend `true` quand le monde a été
 * (re)construit — l'appelant en profite pour invalider les caches de requête.
 */
export function poserLesCreatrices(creatrices: CreatriceDemo[]): boolean {
  const signature = creatrices.map((c) => `${c.id}:${c.slug}`).join("|");
  if (monde !== null && signature === signatureDesCreatrices) return false;
  signatureDesCreatrices = signature;
  construire(creatrices);
  return true;
}

function construire(creatrices: CreatriceDemo[]): void {
  toutAnnuler();
  const conversations = construireLesConversations(creatrices);
  monde = {
    creatrices,
    ordre: conversations.map((c) => c.fiche.id),
    parId: new Map(conversations.map((c) => [c.fiche.id, c])),
    maFile: new Set(),
  };
}

/** Un fan qui ARRIVE pendant le shift : sa conversation naît maintenant. */
export function ajouterUneConversation(conv: ConversationDemo): void {
  if (!monde) return;
  monde.ordre.push(conv.fiche.id);
  monde.parId.set(conv.fiche.id, conv);
}

/** Ma file, telle qu'elle est maintenant. Vide hors monde. */
export function maFile(): Set<string> {
  return monde?.maFile ?? new Set();
}

/** Le monde courant, ou `null` tant que les créatrices ne sont pas arrivées. */
export function leMonde(): Monde | null {
  return monde;
}

/** Repart de zéro (changement de compte, sortie du mode démo). */
export function oublierLeMonde(): void {
  toutAnnuler();
  monde = null;
  signatureDesCreatrices = "";
}

export function conversation(id: string): ConversationDemo | undefined {
  return monde?.parId.get(id);
}

/* ── Qui chatte ──────────────────────────────────────────────────────────── */

/** L'identité posée sur les bulles envoyées depuis le cockpit. Sans elle, la
 *  bulle bascule sur l'avatar de Cyrano au retour du POST — un clignotement
 *  qui se voit à l'image. */
let operatrice: { workosUserId: string | null; email: string | null } = {
  workosUserId: null,
  email: null,
};

export function poserLoperatrice(qui: {
  workosUserId: string | null;
  email: string | null;
}): void {
  operatrice = qui;
}

export function loperatrice(): { workosUserId: string | null; email: string | null } {
  return operatrice;
}

export function toutesLesConversations(): ConversationDemo[] {
  const courant = monde;
  if (!courant) return [];
  return courant.ordre
    .map((id) => courant.parId.get(id))
    .filter((c): c is ConversationDemo => c !== undefined);
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Fabriques de formes                                                        */
/* ────────────────────────────────────────────────────────────────────────── */

let compteur = 0;
/** Identifiant unique, préfixé `demo-` : dans une capture d'écran comme dans
 *  les outils de dev, on doit reconnaître une donnée de démonstration d'un
 *  coup d'oeil. */
export function idDemo(prefixe: string): string {
  compteur += 1;
  return `demo-${prefixe}-${compteur}`;
}

export function ilYA(minutes: number): string {
  return new Date(Date.now() - minutes * 60_000).toISOString();
}

/** Un message, tous champs par défaut posés — on ne renseigne que ce qui
 *  compte pour la scène. */
export function message(
  partiel: Partial<ApiMessage> & Pick<ApiMessage, "direction" | "created_at">,
): ApiMessage {
  const direction = partiel.direction;
  return {
    id: partiel.id ?? idDemo("msg"),
    direction,
    message_type: partiel.message_type ?? "text",
    content: partiel.content ?? null,
    /**
     * 🩸 Ce champ valait `null` EN DUR, et c'est lui qui décide si l'image
     * s'affiche — pas `media_url`. Le fil dérive son type de média de
     * `m.media_file_id ? mediaKindOf(m.message_type) : null`
     * (`panels/fan/views.tsx`), et sans `kind` le bloc image n'est jamais
     * rendu. Résultat le 20-08-2026 : la carte du menu et les médias du Vault
     * partaient bien, la bulle était là, et il n'y avait rien dedans.
     *
     * Une URL sans identifiant ne s'affiche donc pas : les deux vont ensemble.
     */
    media_file_id: partiel.media_file_id ?? null,
    media_url: partiel.media_url ?? null,
    // 🪦 Cyrano n'écrit plus (Kayla, 07-10-2026 : « Cyrano, on l'utilise
    // pas, c'est du legacy »). Ce qui part vers le fan vient du chatteur, ou
    // d'un flow qu'il a lancé — jamais d'un bot.
    is_ai_generated: partiel.is_ai_generated ?? false,
    sender_source: partiel.sender_source ?? (direction === "in" ? "fan" : "operator"),
    flow_id: partiel.flow_id ?? null,
    created_at: partiel.created_at,
    reply_to_message_id: null,
    telegram_message_id: partiel.telegram_message_id ?? 400_000 + compteur,
    delivered: partiel.delivered ?? true,
    payment_button: partiel.payment_button ?? null,
    paid_media: partiel.paid_media ?? null,
    manual_payment: partiel.manual_payment ?? null,
    game_pack: partiel.game_pack ?? null,
    operator_workos_user_id:
      partiel.operator_workos_user_id ??
      (direction === "out" && (partiel.sender_source ?? "operator") === "operator"
        ? operatrice.workosUserId
        : null),
    operator_email:
      partiel.operator_email ??
      (direction === "out" && (partiel.sender_source ?? "operator") === "operator"
        ? operatrice.email
        : null),
    traduction: partiel.traduction ?? null,
    traductions: partiel.traductions ?? null,
    langue: partiel.langue ?? null,
    edited_at: null,
    deleted_at: null,
    bot_reaction: partiel.bot_reaction ?? null,
    poll: null,
    entities: [],
    inline_keyboard: null,
  };
}

/**
 * Une bulle du FAN, avec ce qu'elle dit en français quand il écrit une autre
 * langue — comme Cyrano la persiste (`traductions: { fr }` + `langue`). Le
 * fil l'affiche traduite sans rien demander.
 */
export function bulleDuFan(
  conv: ConversationDemo,
  dit: string | Dit,
  created_at: string,
): ApiMessage {
  const d = typeof dit === "string" ? { texte: dit, fr: dit } : dit;
  const etranger = conv.langue !== "fr";
  return message({
    direction: "in",
    created_at,
    content: d.texte,
    sender_source: "fan",
    ...(etranger ? { traductions: { fr: d.fr }, langue: conv.langue } : {}),
  });
}

/** Le profil Telegram du fan — ce que le panneau fan affiche en tête. */
export function profilFan(nom: string): ApiFanProfile {
  compteur += 1;
  return {
    user_id: `demo-user-${compteur}`,
    telegram_id: 500_000_000 + compteur,
    telegram_chat_id: 500_000_000 + compteur,
    username: nom.toLowerCase().replace(/[^a-z0-9]/g, "") || null,
    first_name: nom,
    avatar_url: null,
    is_premium: false,
    created_at: ilYA(60 * 24 * 12),
  };
}

export function noteVide(): ApiFanNote {
  return { body: "", fan_info: {}, updated_by_workos_user_id: null, updated_at: null };
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Le vocabulaire de tags — un seul, partagé par toutes les créatrices        */
/* ────────────────────────────────────────────────────────────────────────── */

interface DefinitionTag {
  nom: string;
  couleur: string;
  systeme: boolean;
}

/** 🪤 « PIN » et « Time Waster » sont SYSTÈME et pourtant posables à la main :
 *  c'est exactement le piège que documente `estPosableALaMain`. On garde donc
 *  `assignable: true` sur eux, sinon la démo montrerait un clic droit privé des
 *  deux seuls tags que Kayla pose vraiment. */
export const TAGS: DefinitionTag[] = [
  { nom: "PIN", couleur: "#f59e0b", systeme: true },
  { nom: "Time Waster", couleur: "#64748b", systeme: true },
  { nom: "VIP", couleur: "#a855f7", systeme: false },
  { nom: "Jeu", couleur: "#22c55e", systeme: false },
  { nom: "IRL", couleur: "#ec4899", systeme: false },
  { nom: "Gros payeur", couleur: "#3b82f6", systeme: false },
];

function idDeTag(nom: string): string {
  return `demo-tag-${nom.toLowerCase().replace(/\s+/g, "-")}`;
}

export function tagLite(nom: string): FanTagLite {
  const def = TAGS.find((t) => t.nom === nom);
  return {
    id: idDeTag(nom),
    name: def?.nom ?? nom,
    color: def?.couleur ?? "#64748b",
    is_system: def?.systeme ?? false,
  };
}

export function catalogueDeTags(creatorId: string): ApiFanTag[] {
  return TAGS.map((t) => ({
    id: idDeTag(t.nom),
    creator_id: creatorId,
    name: t.nom,
    color: t.couleur,
    is_system: t.systeme,
    assignable: true,
    archived_at: null,
    created_at: ilYA(60 * 24 * 90),
    updated_at: ilYA(60 * 24 * 90),
  }));
}

export function assignationsDe(conv: ConversationDemo): ApiFanTagAssignment[] {
  const catalogue = catalogueDeTags(conv.fiche.creator_id);
  return conv.tags
    .map((t) => catalogue.find((c) => c.name === t.name))
    .filter((t): t is ApiFanTag => t !== undefined)
    .map((tag) => ({
      tag,
      assigned_at: ilYA(60 * 6),
      assigned_by_workos_user_id: null,
    }));
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Le détail d'une conversation                                               */
/* ────────────────────────────────────────────────────────────────────────── */

export function detailDe(
  conv: ConversationDemo,
  leger: boolean,
): ApiConversationDetail {
  return {
    id: conv.fiche.id,
    creator_id: conv.fiche.creator_id,
    user_id: conv.fiche.user_id,
    fan: conv.fan,
    last_message_at: conv.fiche.last_message_at,
    message_count: conv.fiche.message_count,
    created_at: conv.fiche.created_at,
    revenue_cents: conv.fiche.revenue_cents,
    has_active_order: conv.fiche.has_active_order,
    is_cyrano_paused: conv.fiche.is_cyrano_paused ?? false,
    workflow_actif: conv.fiche.workflow_actif ?? null,
    signal_workflow: conv.fiche.signal_workflow ?? null,
    content_mode: conv.fiche.content_mode ?? "off",
    messages: conv.messages,
    profile: null,
    narrative: null,
    memory_facts: [],
    memory_commitments: [],
    // Le payload léger vide les champs lourds — comme `chat-lite` côté Cyrano.
    notes: leger ? noteVide() : conv.note,
    invite_link_source: conv.fiche.invite_link_source ?? null,
  };
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Les gestes qui font bouger le monde                                        */
/* ────────────────────────────────────────────────────────────────────────── */

/** Range un message dans le fil ET recale la ligne de la grille. Une seule
 *  fonction pour les deux : c'est la divergence entre le fil et la carte qui
 *  ferait « faux » à l'image. */
export function poserLeMessage(conv: ConversationDemo, msg: ApiMessage): ApiMessage {
  conv.messages = [...conv.messages, msg];
  conv.fiche.message_count += 1;
  conv.fiche.last_message = msg.content ?? "";
  // 🌍 L'aperçu de la liste se lit traduit, comme la bulle (09-10-2026) : sans
  // ce champ, le fan étranger restait en allemand dans la grille.
  conv.fiche.last_message_traductions = msg.traductions ?? null;
  conv.fiche.last_author = msg.direction === "in" ? "fan" : "bot";
  conv.fiche.last_message_at = msg.created_at;
  if (msg.direction === "in") {
    conv.fiche.unread_count = (conv.fiche.unread_count ?? 0) + 1;
  } else {
    conv.fiche.unread_count = 0;
    // Une réponse éteint le clignotant : c'est ce que fait le backend
    // (`needs_attention` effacé dès qu'un humain répond).
    conv.fiche.needs_attention = false;
    conv.fiche.live_signal = null;
  }
  return msg;
}
